/**
 * Lab bet-ticker Flag column — toggle flag + open comment modal.
 * Flag Final mode: flagged rows show a normal flag; click opens category popup
 * (ported to document.body so table `overflow: clip` cannot hide it).
 */

import { Icon } from '@w7/ui'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  FINAL_FLAG_CATEGORIES,
  FINAL_FLAG_CATEGORY_LABEL,
  type FinalFlagCategory,
  type TickerBet,
} from './domain'
import { flagState, flagTitle } from './flagBet'

type MenuPos = { top: number; left: number }

export function FlagCell({
  bet,
  busy,
  canFlag = true,
  canComment = true,
  finalMode = false,
  onToggle,
  onComment,
  onFinalCategory,
  onClearFinal,
}: {
  bet: TickerBet
  busy?: boolean
  canFlag?: boolean
  canComment?: boolean
  /** Lab `/bet-ticker-flag-final` — category picker popup on flagged rows. */
  finalMode?: boolean
  onToggle: (bet: TickerBet) => void
  onComment: (bet: TickerBet) => void
  onFinalCategory?: (bet: TickerBet, category: FinalFlagCategory) => void
  onClearFinal?: (bet: TickerBet) => void
}) {
  const state = flagState(bet)
  const menuId = useId()
  const btnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLSpanElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null)
  /** Lab: category picker whenever flagged on Flag Final (even if already final). */
  const showCategoryMenu =
    finalMode && bet.isFlagged && Boolean(onFinalCategory)
  const categoryLabel =
    bet.isFinalFlagged && bet.finalFlagCategory
      ? FINAL_FLAG_CATEGORY_LABEL[bet.finalFlagCategory as FinalFlagCategory] ??
        bet.finalFlagCategory
      : null

  useLayoutEffect(() => {
    if (!menuOpen || !btnRef.current) {
      setMenuPos(null)
      return
    }
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect()
      if (!r) return
      const menuW = 200
      const left = Math.min(
        Math.max(8, r.left),
        Math.max(8, window.innerWidth - menuW - 8),
      )
      const below = r.bottom + 6
      const above = r.top - 6
      const top =
        below + 44 < window.innerHeight ? below : Math.max(8, above - 44)
      setMenuPos({ top, left })
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    // Defer so the opening click does not immediately close the menu.
    const t = window.setTimeout(() => {
      const onDoc = (e: MouseEvent) => {
        const n = e.target as Node
        if (btnRef.current?.contains(n) || menuRef.current?.contains(n)) return
        setMenuOpen(false)
      }
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setMenuOpen(false)
      }
      document.addEventListener('mousedown', onDoc)
      document.addEventListener('keydown', onKey)
      cleanup = () => {
        document.removeEventListener('mousedown', onDoc)
        document.removeEventListener('keydown', onKey)
      }
    }, 0)
    let cleanup: (() => void) | undefined
    return () => {
      window.clearTimeout(t)
      cleanup?.()
    }
  }, [menuOpen])

  useEffect(() => {
    setMenuOpen(false)
  }, [bet.id, bet.isFlagged, bet.isFinalFlagged])

  return (
    <span className="w7-bm-flag-cell" onClick={(e) => e.stopPropagation()}>
      <span className="w7-bm-flag-wrap">
        <button
          ref={btnRef}
          type="button"
          className="w7-bm-flag-btn"
          data-state={state}
          data-category={categoryLabel || undefined}
          title={
            canFlag
              ? categoryLabel
                ? `${flagTitle(bet)} · ${bet.finalFlagCategory}`
                : showCategoryMenu
                  ? 'Choose final flag category'
                  : flagTitle(bet)
              : 'Flag bet (no access)'
          }
          aria-label={
            canFlag
              ? showCategoryMenu
                ? 'Choose final flag category'
                : flagTitle(bet)
              : 'Flag bet (no access)'
          }
          aria-pressed={bet.isFlagged}
          aria-expanded={showCategoryMenu ? menuOpen : undefined}
          aria-controls={showCategoryMenu ? menuId : undefined}
          aria-haspopup={showCategoryMenu ? 'menu' : undefined}
          aria-busy={busy || undefined}
          disabled={busy || !canFlag}
          onClick={() => {
            if (!canFlag) return
            if (showCategoryMenu) {
              setMenuOpen((v) => !v)
              return
            }
            onToggle(bet)
          }}
        >
          {categoryLabel ? (
            <span className="w7-bm-flag-btn-cat" aria-hidden="true">
              {categoryLabel}
            </span>
          ) : null}
        </button>
        {menuOpen && showCategoryMenu && menuPos
          ? createPortal(
              <span
                ref={menuRef}
                id={menuId}
                className="w7-bm-flag-menu"
                role="menu"
                aria-label="Final flag category"
                style={{ top: menuPos.top, left: menuPos.left }}
              >
                {FINAL_FLAG_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    className="w7-bm-flag-cat"
                    data-cat={FINAL_FLAG_CATEGORY_LABEL[cat]}
                    role="menuitem"
                    title={canFlag ? cat : `${cat} (no access)`}
                    aria-label={canFlag ? cat : `${cat} (no access)`}
                    disabled={busy || !canFlag}
                    onClick={() => {
                      onFinalCategory?.(bet, cat)
                      setMenuOpen(false)
                    }}
                  >
                    {FINAL_FLAG_CATEGORY_LABEL[cat]}
                  </button>
                ))}
                {bet.isFinalFlagged && onClearFinal ? (
                  <button
                    type="button"
                    className="w7-bm-flag-cat w7-bm-flag-cat--clear"
                    role="menuitem"
                    title={canFlag ? 'Clear final flag' : 'Clear final flag (no access)'}
                    aria-label={
                      canFlag ? 'Clear final flag' : 'Clear final flag (no access)'
                    }
                    disabled={busy || !canFlag}
                    onClick={() => {
                      onClearFinal(bet)
                      setMenuOpen(false)
                    }}
                  >
                    ×
                  </button>
                ) : null}
              </span>,
              document.body,
            )
          : null}
      </span>
      <button
        type="button"
        className="w7-bm-flag-comment"
        data-has-comment={bet.comment.length > 0 ? '1' : '0'}
        title={
          canComment
            ? bet.comment || 'Add comment'
            : bet.comment
              ? `Comment (view only): ${bet.comment}`
              : 'Comment (no access)'
        }
        aria-label={
          canComment
            ? bet.comment
              ? `Comment: ${bet.comment}`
              : 'Add comment'
            : 'Comment (no access)'
        }
        aria-busy={busy || undefined}
        disabled={busy}
        onClick={() => onComment(bet)}
      >
        <Icon name="comment" size={15} />
      </button>
    </span>
  )
}
